import React, { useEffect } from 'react';
import Layout from '../components/layout/Layout';
import HeroSection from '../components/shared/HeroSection';
import SectionTitle from '../components/shared/SectionTitle';
import SolutionCard from '../components/shared/SolutionCard';
import StepCard from '../components/shared/StepCard';
import ValueCard from '../components/shared/ValueCard';
import CTASection from '../components/shared/CTASection';
import PaymentOptions from '../components/shared/PaymentOptions';
import SEOHead from '../components/shared/SEOHead';
import { ParallaxSection, ParallaxElement, FadeInOnScroll } from '../components/shared/Parallax';
import { heroContent, solutions, workMethod, brandValues, companyInfo } from '../data/mock';
import { pageSEO, generateLocalBusinessSchema } from '../utils/seo';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Factory, Users, Award, CheckCircle, Sparkles, Euro } from 'lucide-react';
import { Button } from '../components/ui/button';

const HomePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Layout>
      <SEOHead
        title={pageSEO.home.title}
        description={pageSEO.home.description}
        keywords={pageSEO.home.keywords}
      />

      {/* Hero Section with Video Carousel */}
      <HeroSection
        title="Giovanni Tende - Produttori Diretti dal 1967"
        subtitle="Gli unici produttori di tende da sole in Toscana. Vieni in azienda a vedere la vera qualità artigianale."
        ctaText={heroContent.home.cta}
        ctaLink="/preventivo"
        videoUrls={[
          "https://customer-assets.emergentagent.com/job_tuscany-tende/artifacts/ocaep2cm_hf_20260217_173729_8541910f-1fd2-4de2-b3e9-7cfe92715649%20%281%29.mp4",
          "https://customer-assets.emergentagent.com/job_828fb547-63e8-4146-aaa2-2aed84a5e0d5/artifacts/brbi3sp3_bioclimatica%20video%20hero%20%2B.mp4",
          "https://customer-assets.emergentagent.com/job_tuscany-tende/artifacts/cz63goib_ScreenRecording_02-13-2026%2012-13-59_1.mov",
          "https://customer-assets.emergentagent.com/job_828fb547-63e8-4146-aaa2-2aed84a5e0d5/artifacts/io4jgfvk_video%20bioclimatica%20sardegna%20vetri.mp4"
        ]}
        imageUrl={heroContent.home.imageUrl}
        showScrollIndicator={true}
        videoDuration={10000}
      />

      {/* I NOSTRI PUNTI DI FORZA Section */}
      <section className="py-20 lg:py-28 bg-gray-900 text-white">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <div className="text-center mb-16">
              <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-sky-400">
                Perché Sceglierci
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light leading-tight">
                I Nostri Punti di Forza
              </h2>
            </div>
          </FadeInOnScroll>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <FadeInOnScroll delay={100}>
              <div className="text-center p-6">
                <div className="w-16 h-16 rounded-full bg-sky-500/20 flex items-center justify-center mx-auto mb-6">
                  <Factory className="w-8 h-8 text-sky-400" />
                </div>
                <h3 className="text-xl font-medium mb-3">Produzione Propria</h3>
                <p className="text-gray-400 font-light leading-relaxed">
                  Unici produttori diretti in Toscana con sartoria e magazzino interno
                </p>
              </div>
            </FadeInOnScroll>
            
            <FadeInOnScroll delay={200}>
              <div className="text-center p-6">
                <div className="w-16 h-16 rounded-full bg-sky-500/20 flex items-center justify-center mx-auto mb-6">
                  <Users className="w-8 h-8 text-sky-400" />
                </div>
                <h3 className="text-xl font-medium mb-3">Installatori Interni</h3>
                <p className="text-gray-400 font-light leading-relaxed">
                  Personale qualificato dipendente, non subappaltatori esterni
                </p>
              </div>
            </FadeInOnScroll>
            
            <FadeInOnScroll delay={300}>
              <div className="text-center p-6">
                <div className="w-16 h-16 rounded-full bg-sky-500/20 flex items-center justify-center mx-auto mb-6">
                  <Award className="w-8 h-8 text-sky-400" />
                </div>
                <h3 className="text-xl font-medium mb-3">60+ Anni di Esperienza</h3>
                <p className="text-gray-400 font-light leading-relaxed">
                  Dal 1967 al servizio dei clienti con passione e professionalità
                </p>
              </div>
            </FadeInOnScroll>
            
            <FadeInOnScroll delay={400}>
              <div className="text-center p-6">
                <div className="w-16 h-16 rounded-full bg-sky-500/20 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-sky-400" />
                </div>
                <h3 className="text-xl font-medium mb-3">Qualità Certificata</h3>
                <p className="text-gray-400 font-light leading-relaxed">
                  Iscritti all'Albo Protezioni Solari e associati ASSITES
                </p>
              </div>
            </FadeInOnScroll>
          </div>
        </div>
      </section>

      {/* Brand Values Section - Eccellenza Artigianale */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <SectionTitle
              label="Perché Sceglierci"
              title="Gli unici con produzione diretta in Toscana"
              description="Non siamo rivenditori. Siamo artigiani con laboratorio, sartoria e magazzino proprio. Vieni a vedere la differenza."
            />
          </FadeInOnScroll>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-12">
            {brandValues.map((value, index) => (
              <FadeInOnScroll key={index} delay={index * 150}>
                <ValueCard value={value} index={index} />
              </FadeInOnScroll>
            ))}
          </div>
          
          {/* Produttori vs Rivenditori */}
          <FadeInOnScroll delay={300}>
            <div className="bg-gradient-to-br from-sky-50 to-blue-50 p-8 lg:p-12 rounded-lg border border-sky-100">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <span className="inline-flex items-center gap-2 px-3 py-1 bg-sky-500 text-white text-sm font-medium rounded-full mb-4">
                    <Factory className="w-4 h-4" />
                    PRODUTTORI DIRETTI
                  </span>
                  <h3 className="text-2xl md:text-3xl font-light text-gray-900 mb-4">
                    La differenza tra <span className="text-sky-500 font-medium">produttori</span> e rivenditori
                  </h3>
                  <p className="text-gray-600 font-light leading-relaxed mb-6">
                    Molti negozi vendono tende da sole, ma <strong>quanti le producono davvero?</strong> 
                    Noi siamo gli unici in Toscana con una vera produzione interna: sartoria, 
                    magazzino tessuti e team di installatori dipendenti. 
                    Questo significa <strong>qualità controllata</strong> e <strong>prezzi imbattibili</strong>.
                  </p>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-center gap-3 text-gray-700">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span><strong>Nessun intermediario</strong> = prezzi più bassi</span>
                    </li>
                    <li className="flex items-center gap-3 text-gray-700">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span><strong>Produzione interna</strong> = qualità garantita</span>
                    </li>
                    <li className="flex items-center gap-3 text-gray-700">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span><strong>Montatori dipendenti</strong> = installazione perfetta</span>
                    </li>
                    <li className="flex items-center gap-3 text-gray-700">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span><strong>Assistenza diretta</strong> = nessun rimbalzo</span>
                    </li>
                  </ul>
                  <Link to="/chi-siamo">
                    <Button className="bg-sky-500 hover:bg-sky-600 text-white px-6 py-3 rounded-none">
                      Scopri il nostro laboratorio
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
                <div className="text-center">
                  <div className="bg-white p-8 rounded-lg shadow-lg">
                    <Award className="w-16 h-16 text-sky-500 mx-auto mb-4" />
                    <h4 className="text-xl font-medium text-gray-900 mb-2">Vieni a trovarci!</h4>
                    <p className="text-gray-600 font-light mb-4">
                      Ti mostriamo la nostra produzione, la sartoria e il magazzino. 
                      Solo vedendo capirai la differenza.
                    </p>
                    <p className="text-sm text-gray-500">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      {companyInfo.address.street}, {companyInfo.address.city}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </FadeInOnScroll>
        </div>
      </section>

      {/* Financing Banner Section */}
      <section className="py-24 lg:py-32 relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://customer-assets.emergentagent.com/job_8f97a621-59c2-46f0-8bec-942072242924/artifacts/jk683ww7_biocliamtica%20vetrate.png)`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
        
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <FadeInOnScroll>
              <div>
                <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-sky-500 to-blue-600 rounded-full mb-8 shadow-lg shadow-sky-500/30">
                  <Sparkles className="w-4 h-4 text-white" />
                  <span className="text-white text-sm font-semibold tracking-wide">NOVITÀ 2026</span>
                </div>
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-light text-white mb-6 leading-tight">
                  Finanziamento a<br />
                  <span className="text-sky-400 font-bold">Tasso Zero</span>
                </h2>
                <p className="text-xl text-gray-200 font-light mb-10 leading-relaxed max-w-lg">
                  Realizza il tuo progetto outdoor senza pensieri. Paga il 50% all'ordine 
                  e il resto in 10 comode rate annuali, <strong className="text-white">senza interessi</strong>.
                </p>
                
                <div className="flex gap-4 mb-10">
                  <div className="bg-white/20 backdrop-blur-md rounded-2xl p-5 text-center min-w-[120px] border border-white/20">
                    <div className="text-4xl font-bold text-white">0%</div>
                    <div className="text-sm text-gray-300 mt-1">TAN e TAEG</div>
                  </div>
                  <div className="bg-white/20 backdrop-blur-md rounded-2xl p-5 text-center min-w-[120px] border border-white/20">
                    <div className="text-4xl font-bold text-white">10</div>
                    <div className="text-sm text-gray-300 mt-1">Rate annuali</div>
                  </div>
                  <div className="bg-white/20 backdrop-blur-md rounded-2xl p-5 text-center min-w-[120px] border border-white/20">
                    <div className="text-4xl font-bold text-white">50%</div>
                    <div className="text-sm text-gray-300 mt-1">Acconto</div>
                  </div>
                </div>
                
                <Link to="/giovanni-tende-smart">
                  <Button className="bg-sky-500 hover:bg-sky-400 text-white px-10 py-5 text-lg rounded-full shadow-xl shadow-sky-500/40 hover:shadow-sky-400/50 transition-all duration-300 hover:scale-105">
                    Scopri come funziona
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </FadeInOnScroll>
            
            <FadeInOnScroll delay={200}>
              <div className="relative">
                <div className="absolute -inset-2 bg-gradient-to-r from-sky-500 via-blue-500 to-cyan-400 rounded-3xl blur-lg opacity-40 animate-pulse" />
                <div className="relative bg-white rounded-3xl p-8 shadow-2xl">
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-sky-100 rounded-2xl mb-4">
                      <Euro className="w-8 h-8 text-sky-500" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">Esempio pratico</h3>
                    <p className="text-gray-500">Pergola Bioclimatica da €10.000</p>
                  </div>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-4 border-b border-gray-100">
                      <span className="text-gray-600">Acconto (50%)</span>
                      <span className="text-2xl font-bold text-gray-900">€ 5.000</span>
                    </div>
                    <div className="flex justify-between items-center py-4 border-b border-gray-100">
                      <span className="text-gray-600">Importo finanziato</span>
                      <span className="text-2xl font-bold text-sky-500">€ 5.000</span>
                    </div>
                    <div className="bg-gradient-to-r from-sky-50 to-blue-50 -mx-8 px-8 py-5 mt-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="text-gray-900 font-semibold block text-lg">Rata annuale</span>
                          <span className="text-gray-500 text-sm">per 10 anni</span>
                        </div>
                        <span className="text-4xl font-black text-sky-500">€ 500</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Alla pratica ci pensiamo noi - More Attractive */}
                  <div className="mt-6 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-green-500 rounded-xl" />
                    <div className="relative p-5 flex items-center gap-4">
                      <div className="flex-shrink-0 w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-8 h-8 text-white" />
                      </div>
                      <div className="text-white">
                        <p className="font-bold text-lg">Alla pratica ci pensiamo noi!</p>
                        <p className="text-white/80 text-sm">Zero burocrazia, gestiamo tutto noi</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </FadeInOnScroll>
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-24 lg:py-32 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <SectionTitle
              label="Le Nostre Soluzioni"
              title="Ogni spazio merita la sua soluzione"
              description="Progettiamo e realizziamo sistemi di copertura e protezione solare su misura, per ogni esigenza architettonica."
            />
          </FadeInOnScroll>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {solutions.map((solution, index) => (
              <FadeInOnScroll key={solution.id} delay={index * 100}>
                <SolutionCard solution={solution} index={index} />
              </FadeInOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Work Method Section */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <SectionTitle
              label="Il Nostro Metodo"
              title="Un percorso chiaro verso l'eccellenza"
              description="Ogni progetto segue un processo strutturato che garantisce risultati impeccabili e soddisfazione totale."
            />
          </FadeInOnScroll>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {workMethod.map((step, index) => (
              <FadeInOnScroll key={step.step} delay={index * 150}>
                <StepCard step={step} index={index} />
              </FadeInOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Parallax Visual Section */}
      <ParallaxSection
        imageUrl="https://customer-assets.emergentagent.com/job_tuscany-tende/artifacts/qcp5qq37_hf_20260218_082134_eb2ab031-2a9d-4ab7-835d-a2d9cd7416a1.png"
        speed={0.3}
        overlayOpacity={0.45}
        minHeight="70vh"
      >
        <div className="h-[70vh] flex items-center justify-center">
          <div className="text-center px-6">
            <ParallaxElement speed={0.1} direction="up">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white mb-4">
                Dove il design incontra la funzionalità
              </h2>
            </ParallaxElement>
            <ParallaxElement speed={0.15} direction="up">
              <p className="text-lg text-white/80 font-light max-w-2xl mx-auto">
                Creiamo spazi esterni che riflettono il tuo stile di vita e valorizzano la tua proprietà.
              </p>
            </ParallaxElement>
          </div>
        </div>
      </ParallaxSection>

      {/* Aree Servite - SEO Section */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <FadeInOnScroll>
              <div className="relative">
                <img 
                  src="https://customer-assets.emergentagent.com/job_64076d9f-5df4-4045-b4f3-caa897c80892/artifacts/ddj00xrj_fotobionottetoscana.jpeg" 
                  alt="Pergola bioclimatica in Toscana - Giovanni Tende"
                  className="w-full rounded-lg shadow-lg"
                />
              </div>
            </FadeInOnScroll>
            
            {/* Content */}
            <FadeInOnScroll delay={200}>
              <div>
                <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-sky-500">
                  Dove Operiamo
                </span>
                <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
                  Tende da Sole e Pergole in Tutta la Toscana
                </h2>
                
                <div className="space-y-6">
                  {/* Zona Pisa */}
                  <div className="border-l-4 border-sky-500 pl-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Province di Pisa, Livorno, Lucca e Pistoia
                    </h3>
                    <p className="text-gray-600 font-light leading-relaxed">
                      Installiamo <strong>tende da sole, cappottine, tende da interno</strong> e tutti i nostri prodotti 
                      nelle zone limitrofe alla nostra sede di Pisa. Servizio rapido e assistenza sempre vicina.
                    </p>
                  </div>
                  
                  {/* Tutta Toscana */}
                  <div className="border-l-4 border-gray-900 pl-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Tutta la Toscana
                    </h3>
                    <p className="text-gray-600 font-light leading-relaxed">
                      Per progetti più importanti come <strong>pergotende, pergole bioclimatiche e vetrate panoramiche</strong>, 
                      ci spostiamo in tutta la regione: Firenze, Siena, Arezzo, Grosseto, Massa-Carrara e oltre.
                    </p>
                  </div>
                </div>
                
                <div className="mt-8 flex flex-col sm:flex-row gap-4">
                  <Link to="/preventivo">
                    <Button className="bg-sky-500 hover:bg-sky-600 text-white px-6 py-3 rounded-none w-full sm:w-auto">
                      Richiedi Preventivo Gratuito
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <a href={`tel:${companyInfo.phone}`} className="inline-flex items-center justify-center gap-2 text-gray-700 hover:text-sky-500 font-medium transition-colors">
                    <MapPin className="w-5 h-5" />
                    Chiama per info: {companyInfo.phone}
                  </a>
                </div>
              </div>
            </FadeInOnScroll>
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <div className="text-center mb-16">
              <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-sky-500">
                Le Nostre Recensioni
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-gray-900 mb-4">
                Cosa dicono i nostri clienti
              </h2>
              <div className="flex items-center justify-center gap-3 mt-6">
                <div className="flex items-center gap-1">
                  {[1,2,3,4,5].map((star) => (
                    <svg key={star} className={`w-6 h-6 ${star <= 4 ? 'text-yellow-400' : 'text-yellow-400'}`} fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <span className="text-2xl font-semibold text-gray-900">4,7</span>
                <span className="text-gray-500">su 138 recensioni Google</span>
              </div>
            </div>
          </FadeInOnScroll>
          
          {/* Reviews Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-12">
            <FadeInOnScroll delay={100}>
              <div className="bg-gray-50 p-6 lg:p-8 rounded-lg border border-gray-100">
                <div className="flex items-center gap-1 mb-4">
                  {[1,2,3,4,5].map((star) => (
                    <svg key={star} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-700 font-light leading-relaxed mb-4">
                  "Azienda storica, bellissimo showroom, personale qualificato. Consiglio vivamente per qualità e professionalità."
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-sky-500 flex items-center justify-center text-white font-medium">
                    D
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Dario L.</p>
                    <p className="text-sm text-gray-500">Google Review</p>
                  </div>
                </div>
              </div>
            </FadeInOnScroll>
            
            <FadeInOnScroll delay={200}>
              <div className="bg-gray-50 p-6 lg:p-8 rounded-lg border border-gray-100">
                <div className="flex items-center gap-1 mb-4">
                  {[1,2,3,4,5].map((star) => (
                    <svg key={star} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-700 font-light leading-relaxed mb-4">
                  "Buona scelta di stoffe, personale gentile e preparato, buoni prezzi. Ho fatto installare una pergotenda e sono molto soddisfatto."
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center text-white font-medium">
                    M
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Massimo B.</p>
                    <p className="text-sm text-gray-500">Google Review</p>
                  </div>
                </div>
              </div>
            </FadeInOnScroll>
            
            <FadeInOnScroll delay={300}>
              <div className="bg-gray-50 p-6 lg:p-8 rounded-lg border border-gray-100">
                <div className="flex items-center gap-1 mb-4">
                  {[1,2,3,4,5].map((star) => (
                    <svg key={star} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-700 font-light leading-relaxed mb-4">
                  "Ottima esperienza! Dalla consulenza al montaggio tutto perfetto. Tende di qualità e installatori competenti. Super consigliato!"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center text-white font-medium">
                    L
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Laura S.</p>
                    <p className="text-sm text-gray-500">Google Review</p>
                  </div>
                </div>
              </div>
            </FadeInOnScroll>
          </div>
          
          {/* CTA to Google Reviews */}
          <FadeInOnScroll delay={400}>
            <div className="text-center">
              <a 
                href="https://www.google.com/search?q=Tappezzeria+Giovanni+recensioni" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sky-500 hover:text-sky-600 font-medium transition-colors"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Vedi tutte le 138 recensioni su Google
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </FadeInOnScroll>
        </div>
      </section>

      {/* Payment Options - Full */}
      <section className="py-16 lg:py-20 bg-white border-t border-gray-100">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <div className="text-center mb-8">
              <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-sky-500">
                Pagamenti Flessibili
              </span>
              <h2 className="text-2xl md:text-3xl font-light text-gray-900">
                Scegli come pagare
              </h2>
            </div>
            <PaymentOptions variant="full" className="max-w-4xl mx-auto" />
          </FadeInOnScroll>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection
        title="Inizia il tuo progetto"
        description="Contattaci per una consulenza gratuita e senza impegno. Scopri come possiamo trasformare il tuo spazio esterno."
        variant="light"
      />
    </Layout>
  );
};

export default HomePage;
